/**
 * Portfolio-playbook tour demo layer.
 *
 * While the guided tour is running, the service layer (clientApi, api/client)
 * returns the canned data below instead of hitting the network, so no tour step
 * depends on the real selected client's data. Nothing here is persisted and no
 * network calls are made for the demo client.
 *
 * This module intentionally has no imports so it can be safely referenced from
 * the low-level API helpers without creating circular dependencies.
 */

export const DEMO_USER_CODE = 'TOURDEMO';
export const DEMO_CLIENT_ID = 'tour-demo-client';

// --- Active-state singleton -------------------------------------------------

const state = {
  active: false,
  providerId: null,
};

export function setTourDemoActive(active, providerId = null) {
  const nextActive = !!active;
  const nextProvider = nextActive
    ? (providerId !== null ? providerId : state.providerId)
    : null;
  const changed = state.active !== nextActive || state.providerId !== nextProvider;
  state.active = nextActive;
  state.providerId = nextProvider;
  // Only notify when something actually changed — re-firing remounts the
  // dashboard table and drops the tour's highlight / click listener mid-step.
  if (changed && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('tour-demo-changed', { detail: { active: state.active } }));
  }
}

export function isTourDemoActive() {
  return state.active;
}

export function getDemoProviderId() {
  return state.providerId;
}

/** Prepend the tour example client when demo mode is on; strip it when off. */
export function withTourDemoClient(clients = []) {
  const list = Array.isArray(clients) ? clients : [];
  const withoutDemo = list.filter(
    (c) => c?.user_code !== DEMO_USER_CODE && String(c?.id) !== DEMO_CLIENT_ID
  );
  if (!state.active) return withoutDemo;
  return [buildDemoClient(state.providerId), ...withoutDemo];
}

export function isDemoUserCode(userCode) {
  return state.active && userCode === DEMO_USER_CODE;
}

export function isDemoClientId(clientId) {
  return state.active && clientId === DEMO_CLIENT_ID;
}

export function isDemoMealPlanId(mealPlanId) {
  return state.active && String(mealPlanId || '').startsWith('demo-plan-');
}

/** True for the tour example client even when demo mode is off (e.g. stale localStorage). */
export function looksLikeDemoClient(client) {
  if (!client) return false;
  return String(client.id) === DEMO_CLIENT_ID || client.user_code === DEMO_USER_CODE;
}

// --- Date helpers -----------------------------------------------------------

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
}

function isoDate(n) {
  return daysAgo(n).toISOString().split('T')[0];
}

function isoStamp(n) {
  return daysAgo(n).toISOString();
}

// --- Demo client ------------------------------------------------------------

export function buildDemoClient(providerId = state.providerId) {
  return {
    id: DEMO_CLIENT_ID,
    user_code: DEMO_USER_CODE,
    provider_id: providerId || null,
    full_name: 'Maya Levi (Tour example)',
    name: 'Maya Levi (Tour example)',
    email: 'maya.levi@example.com',
    phone_number: '+972-52-555-0148',
    whatsapp_number: '+972-52-555-0148',
    language: 'en',
    user_language: 'en',
    region: 'Center',
    city: 'Tel Aviv',
    timezone: 'Asia/Jerusalem',
    date_of_birth: '1992-04-18',
    age: 34,
    gender: 'female',
    nursing_status: false,
    height_cm: 168,
    weight_kg: 74.1,
    medical_conditions: [],
    Activity_level: 'moderately_active',
    goal: 'lose',
    food_allergies: [],
    food_limitations: ['No late-night snacking'],
    client_preference: {
      likes: ['Greek yogurt', 'Salmon', 'Roasted vegetables'],
      dislikes: ['Liver'],
    },
    first_meal_time: '08:00',
    last_meal_time: '20:00',
    activated: true,
    is_blocked: false,
    onboarding_done: true,
    // Dashboard onboarding column (matches GET /admin/dashboard shape)
    lastStep: { num: 19, label: 'Meal planning' },
    onboardingStuckAt: null,
    isOnboardingFinished: true,
    subscription_status: 'active',
    subscription_type: 'premium',
    subscription: {
      status: 'active',
      subscription_type: 'premium',
      amount_total: 14900,
      currency: 'ILS',
      current_period_end: isoStamp(-25),
      cancel_at_period_end: false,
    },
    status: 'active',
    number_of_meals: 3,
    daily_target_total_calories: 1650,
    daily_calories: 1650,
    macros: { protein: 115, carbs: 155, fat: 55 },
    // Admin-dashboard enrichment so activate-phase steps and the healthy-clients
    // bulk composer have a row to work with.
    engagementPulse: 'healthy',
    lastInteractionTime: isoStamp(0),
    mealPlanStatus: 'active',
    mealPlanStatusUpdated: isoStamp(3),
    mealPlanName: 'Balanced week — Maya',
    workoutPlanStatus: 'active',
    workoutPlanStatusUpdated: isoStamp(5),
    lastWeightUpdate: isoDate(1),
    // Same lines as getDemoMessages(), newest last.
    chatPreview: getDemoMessages().slice(-4).map((m) => ({
      role: m.role,
      content: m.content || m.message,
      created_at: m.created_at,
    })),
    provider_name: 'You',
  };
}

// --- Meal plans -------------------------------------------------------------

const round1 = (n) => Math.round(n * 10) / 10;

// One plan ingredient, in the fields the meal-plan detail view reads
// (item, household_measure, portionSI(gram), calories, protein, carbs, fat).
// name/quantity are kept for older readers.
function ing(item, householdMeasure, grams, calories, protein, carbs, fat) {
  return {
    item,
    name: item,
    quantity: `${grams} g`,
    household_measure: householdMeasure,
    'portionSI(gram)': grams,
    calories,
    protein,
    carbs,
    fat,
  };
}

function sumNutrition(ingredients) {
  const t = ingredients.reduce(
    (acc, i) => ({
      calories: acc.calories + i.calories,
      protein: acc.protein + i.protein,
      carbs: acc.carbs + i.carbs,
      fat: acc.fat + i.fat,
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  );
  return { calories: Math.round(t.calories), protein: round1(t.protein), carbs: round1(t.carbs), fat: round1(t.fat) };
}

// A dish whose nutrition is always the sum of its ingredients.
function dish(fields, ingredients) {
  return { ...fields, nutrition: sumNutrition(ingredients), ingredients };
}

const demoMeals = [
  {
    meal: 'Breakfast',
    main: dish({ meal_name: 'Breakfast', meal_title: 'Greek yogurt bowl with berries and granola' }, [
      ing('Greek yogurt 2%', '1 cup', 200, 150, 20, 8, 4),
      ing('Mixed berries', '1 cup', 120, 60, 1, 14, 0),
      ing('Granola', '1/3 cup', 35, 160, 4, 21, 6),
      ing('Honey', '2 tsp', 10, 30, 0, 8, 0),
      ing('Almond butter', '1 tsp', 8, 50, 2, 1, 4),
    ]),
    alternatives: [
      dish({ meal_name: 'Breakfast', meal_title: 'Veggie omelette with whole grain toast' }, [
        ing('Eggs', '3 large', 150, 215, 19, 1, 15),
        ing('Spinach', '1 cup', 50, 12, 1, 2, 0),
        ing('Whole grain bread', '2 slices', 70, 170, 8, 30, 2),
        ing('Feta cheese', '2 tbsp', 30, 80, 4, 1, 6),
      ]),
    ],
  },
  {
    meal: 'Lunch',
    main: dish({ meal_name: 'Lunch', meal_title: 'Grilled chicken, quinoa and roasted vegetables' }, [
      ing('Chicken breast, grilled', '1 fillet', 120, 198, 37, 0, 4),
      ing('Quinoa, cooked', '3/4 cup', 140, 168, 6, 30, 3),
      ing('Roasted vegetables', '1.5 cups', 200, 116, 3, 18, 3),
      ing('Olive oil', '2 tsp', 10, 88, 0, 0, 10),
      ing('Whole wheat pita', '1/2 pita', 30, 80, 3, 15, 1),
    ]),
    alternatives: [
      dish({ meal_name: 'Lunch', meal_title: 'Salmon poke bowl with brown rice' }, [
        ing('Salmon', '1 fillet', 140, 290, 28, 0, 19),
        ing('Brown rice, cooked', '3/4 cup', 150, 165, 4, 34, 1),
        ing('Edamame', '1/2 cup', 80, 97, 9, 7, 4),
        ing('Cucumber and seaweed', '1 cup', 100, 20, 1, 4, 0),
      ]),
    ],
  },
  {
    meal: 'Dinner',
    main: dish({ meal_name: 'Dinner', meal_title: 'Lean beef stir fry with rice noodles' }, [
      ing('Lean beef strips', '1 portion', 130, 205, 30, 0, 9),
      ing('Rice noodles, cooked', '1 cup', 140, 150, 3, 34, 0),
      ing('Stir fry vegetables', '2 cups', 200, 70, 4, 12, 1),
      ing('Sesame oil', '1 tsp', 5, 44, 0, 0, 5),
      ing('Soy sauce, reduced sodium', '1 tbsp', 15, 10, 2, 1, 0),
      ing('Cashews', '1 tbsp', 10, 55, 2, 3, 4),
    ]),
    alternatives: [
      dish({ meal_name: 'Dinner', meal_title: 'Baked cod with sweet potato' }, [
        ing('Cod fillet', '1 large fillet', 200, 164, 36, 0, 1),
        ing('Sweet potato, baked', '1 medium', 200, 180, 4, 41, 0),
        ing('Green beans', '1 cup', 120, 37, 2, 8, 0),
        ing('Olive oil', '1 tsp', 5, 44, 0, 0, 5),
        ing('Lemon tahini sauce', '1 tbsp', 15, 90, 3, 3, 8),
      ]),
    ],
  },
];

// Daily target the plan is built for (matches buildDemoClient()).
const DEMO_TARGETS = { calories: 1650, protein: 115, carbs: 155, fat: 55 };

const demoMealPlanFull = {
  meals: demoMeals,
  note: 'Balanced plan tuned to a gentle calorie deficit. Alternatives keep it flexible.',
  // What the main dishes actually add up to.
  totals: sumNutrition(demoMeals.map((m) => m.main.nutrition)),
};

/** Saved meal slots (GET /clients/:id/meal-plan-structure), onboarding format. */
export function getDemoMealPlanStructure() {
  const structure = demoMeals.map((m) => ({
    meal: m.meal,
    description: '',
    calories: m.main.nutrition.calories,
    calories_pct: Math.round((m.main.nutrition.calories / DEMO_TARGETS.calories) * 100),
  }));
  return { structure, numberOfMeals: structure.length };
}

// List rows (summary shape returned by GET /meal-plans/:userCode).
export const demoMealPlans = [
  {
    id: 'demo-plan-active',
    meal_plan_name: 'Balanced deficit plan',
    meal_plan: demoMealPlanFull,
    status: 'active',
    active_from: isoDate(7),
    active_until: isoDate(-14),
    active_days: [0, 1, 2, 3, 4, 5, 6],
    daily_total_calories: DEMO_TARGETS.calories,
    macros_target: { protein: DEMO_TARGETS.protein, carbs: DEMO_TARGETS.carbs, fat: DEMO_TARGETS.fat },
    created_at: isoStamp(9),
    updated_at: isoStamp(7),
  },
  {
    id: 'demo-plan-scheduled',
    meal_plan_name: 'Higher protein block',
    meal_plan: demoMealPlanFull,
    status: 'scheduled',
    active_from: isoDate(-14),
    active_until: isoDate(-42),
    active_days: [1, 3, 5],
    daily_total_calories: 1720,
    macros_target: { protein: 135, carbs: 150, fat: 52 },
    created_at: isoStamp(4),
    updated_at: isoStamp(4),
  },
  {
    id: 'demo-plan-draft',
    meal_plan_name: 'Maintenance draft',
    meal_plan: demoMealPlanFull,
    status: 'draft',
    active_from: null,
    active_until: null,
    active_days: [],
    daily_total_calories: 1950,
    macros_target: { protein: 120, carbs: 190, fat: 62 },
    created_at: isoStamp(1),
    updated_at: isoStamp(1),
  },
];

// Full plan (shape returned by GET /meal-plans/id/:id).
const demoMealPlanById = demoMealPlans.reduce((acc, plan) => {
  acc[plan.id] = {
    id: plan.id,
    user_code: DEMO_USER_CODE,
    meal_plan_name: plan.meal_plan_name,
    meal_plan: plan.meal_plan,
    status: plan.status,
    active_from: plan.active_from,
    active_until: plan.active_until,
    active_days: plan.active_days,
    daily_total_calories: plan.daily_total_calories,
    macros_target: plan.macros_target,
    created_at: plan.created_at,
    updated_at: plan.updated_at,
  };
  return acc;
}, {});

export function getDemoMealPlans() {
  return demoMealPlans.map((p) => ({ ...p }));
}

export function getDemoMealPlanById(id) {
  const plan = demoMealPlanById[id] || demoMealPlanById['demo-plan-active'];
  return JSON.parse(JSON.stringify(plan));
}

// --- Weight logs ------------------------------------------------------------

export function getDemoWeightLogs() {
  const points = [
    { d: 56, w: 78.4, bf: 30.1, waist: 92 },
    { d: 49, w: 77.9, bf: 29.7, waist: 91 },
    { d: 42, w: 77.1, bf: 29.2, waist: 90 },
    { d: 35, w: 76.6, bf: 28.8, waist: 89 },
    { d: 28, w: 75.8, bf: 28.3, waist: 88 },
    { d: 21, w: 75.3, bf: 27.9, waist: 87 },
    { d: 14, w: 74.7, bf: 27.4, waist: 86 },
    { d: 7, w: 74.3, bf: 27.1, waist: 85.5 },
    { d: 1, w: 74.1, bf: 27.0, waist: 85 },
  ];
  return points.map((p, i) => ({
    id: `demo-weight-${i}`,
    user_id: DEMO_CLIENT_ID,
    user_code: DEMO_USER_CODE,
    measurement_date: isoDate(p.d),
    weight_kg: p.w,
    body_fat_percentage: p.bf,
    waist_circumference_cm: p.waist,
    hip_circumference_cm: p.waist + 12,
    arm_circumference_cm: 31,
    neck_circumference_cm: 35,
    height_cm: 168,
    notes: null,
  }));
}

// --- Food logs --------------------------------------------------------------

/** `daysBack` days ago at hh:mm local time, never later than a few minutes ago. */
function atTime(daysBack, hh, mm) {
  const d = daysAgo(daysBack);
  d.setHours(hh, mm, 0, 0);
  const latest = Date.now() - 5 * 60 * 1000;
  return d.getTime() > latest ? new Date(latest) : d;
}

function localDate(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// One logged item, in the shape the food-log drawer and Food logs page read.
function foodItem(name, portion, calories, protein, carbs, fat) {
  return {
    name,
    portion_estimate: portion,
    macros: { calories, protein_g: protein, carbs_g: carbs, fat_g: fat },
  };
}

const DEMO_FOOD_MEALS = {
  breakfast: {
    meal_label: 'Breakfast',
    time: [8, 10],
    items: [
      foodItem('Greek yogurt 2%', '200 g', 150, 20, 8, 4),
      foodItem('Mixed berries', '1 cup (120 g)', 60, 1, 14, 0),
      foodItem('Granola', '1/3 cup (35 g)', 160, 4, 21, 6),
    ],
    plan_score: 9,
    plan_score_reason: 'Matches the planned yogurt bowl; portion close to plan.',
    health_score: 8,
    health_score_reason: 'High protein, fibre from berries; granola adds some sugar.',
  },
  lunch: {
    meal_label: 'Lunch',
    time: [13, 5],
    items: [
      foodItem('Grilled chicken breast', '120 g', 198, 37, 0, 4),
      foodItem('Quinoa', '3/4 cup (140 g)', 168, 6, 30, 3),
      foodItem('Roasted vegetables', '1.5 cups (200 g)', 116, 3, 18, 3),
      foodItem('Olive oil', '2 tsp', 88, 0, 0, 10),
    ],
    plan_score: 9,
    plan_score_reason: 'The planned lunch, skipped the pita.',
    health_score: 9,
    health_score_reason: 'Lean protein, whole grain and plenty of vegetables.',
  },
  dinner: {
    meal_label: 'Dinner',
    time: [19, 40],
    items: [
      foodItem('Lean beef strips', '130 g', 205, 30, 0, 9),
      foodItem('Rice noodles', '1 cup (140 g)', 150, 3, 34, 0),
      foodItem('Stir fry vegetables', '200 g', 70, 4, 12, 1),
      foodItem('Sesame oil', '1 tsp', 44, 0, 0, 5),
    ],
    plan_score: 8,
    plan_score_reason: 'Planned stir fry; no cashews this time.',
    health_score: 7,
    health_score_reason: 'Balanced plate; the sauce adds sodium.',
  },
  dinnerCod: {
    meal_label: 'Dinner',
    time: [19, 40],
    items: [
      foodItem('Baked cod', '200 g', 164, 36, 0, 1),
      foodItem('Sweet potato', '1 medium (200 g)', 180, 4, 41, 0),
      foodItem('Green beans', '1 cup (120 g)', 37, 2, 8, 0),
      foodItem('Olive oil', '1 tsp', 44, 0, 0, 5),
    ],
    plan_score: 9,
    plan_score_reason: 'The listed dinner alternative, swapped after checking with the coach.',
    health_score: 9,
    health_score_reason: 'Lean fish, complex carbs and vegetables.',
  },
  dinnerPizza: {
    meal_label: 'Dinner',
    time: [20, 30],
    items: [
      foodItem('Margherita pizza', '2 slices (220 g)', 540, 22, 66, 20),
      foodItem('Side salad', '1 bowl (150 g)', 40, 2, 7, 0),
      foodItem('Diet cola', '330 ml', 1, 0, 0, 0),
    ],
    plan_score: 5,
    plan_score_reason: 'Off-plan dinner; calories close to the planned meal.',
    health_score: 4,
    health_score_reason: 'Refined carbs and saturated fat, low protein.',
  },
};

// Days back -> meals logged that day. Today has breakfast only.
const DEMO_FOOD_DAYS = [
  [4, ['breakfast', 'lunch', 'dinnerPizza']],
  [3, ['breakfast', 'lunch', 'dinner']],
  [2, ['breakfast', 'lunch', 'dinnerCod']],
  [1, ['breakfast', 'lunch', 'dinner']],
  [0, ['breakfast']],
];

/** Food logs, oldest first. Totals are always the sum of the items. */
export function getDemoFoodLogs() {
  const logs = [];
  DEMO_FOOD_DAYS.forEach(([day, mealKeys]) => {
    mealKeys.forEach((key, idx) => {
      const m = DEMO_FOOD_MEALS[key];
      const when = atTime(day, m.time[0], m.time[1]);
      const sum = (field) => round1(m.items.reduce((acc, i) => acc + i.macros[field], 0));
      logs.push({
        id: `demo-food-${day}-${idx}`,
        user_id: DEMO_CLIENT_ID,
        user_code: DEMO_USER_CODE,
        meal_label: m.meal_label,
        food_items: m.items.map((i) => ({ ...i, macros: { ...i.macros } })),
        total_calories: Math.round(sum('calories')),
        total_protein_g: sum('protein_g'),
        total_carbs_g: sum('carbs_g'),
        total_fat_g: sum('fat_g'),
        plan_score: m.plan_score,
        plan_score_reason: m.plan_score_reason,
        health_score: m.health_score,
        health_score_reason: m.health_score_reason,
        dietary_warnings: [],
        log_date: localDate(when),
        created_at: when.toISOString(),
        image_url: null,
        has_image: false,
      });
    });
  });
  return logs;
}

// --- Chat -------------------------------------------------------------------

const DEMO_CONVERSATION_ID = 'demo-conversation';

export function getDemoConversations() {
  return [
    { id: DEMO_CONVERSATION_ID, started_at: atTime(4, 20, 50).toISOString(), user_id: DEMO_CLIENT_ID },
  ];
}

// Lines up with getDemoFoodLogs() and getDemoTrainingLogs(): pizza night 4 days ago,
// yogurt-bowl lunch 3 days ago, cod swap + training session 2 days ago. Oldest first.
export function getDemoMessages() {
  const msg = (id, role, text, when) => ({
    id: `demo-msg-${id}`,
    conversation_id: DEMO_CONVERSATION_ID,
    role,
    content: role === 'user' ? text : null,
    message: role === 'user' ? null : text,
    attachments: null,
    created_at: when.toISOString(),
    topic: null,
  });
  return [
    msg(1, 'user', 'Pizza night with friends. Logged it anyway!', atTime(4, 20, 50)),
    msg(2, 'assistant', 'Thanks for logging it. One meal does not undo the week, back to the plan tomorrow.', atTime(4, 20, 52)),
    msg(3, 'user', 'Logged lunch. The yogurt bowl this morning kept me full right up to lunch.', atTime(3, 13, 20)),
    msg(4, 'assistant', 'Love it. That yogurt bowl keeps protein high early in the day. Keep it up!', atTime(3, 13, 22)),
    msg(5, 'user', 'Quick one, can I swap dinner for the baked cod option tonight?', atTime(2, 11, 45)),
    msg(6, 'assistant', 'Absolutely, the baked cod with sweet potato is a listed dinner alternative and hits the same targets.', atTime(2, 11, 47)),
    msg(7, 'user', "Thanks coach, felt great after today's session!", atTime(2, 18, 20)),
    msg(8, 'assistant', 'Great work. Your squats are moving up nicely. Refuel with dinner and rest well.', atTime(2, 18, 25)),
  ];
}

// --- Training ---------------------------------------------------------------

// One workout in plan_structure.workouts[]. Each exercise row:
// [name, rest_seconds, min_reps, max_reps, target_rpe, target_weight, coach_notes]; 3 working sets each.
function demoWorkout(workoutId, dayName, recommendedDay, order, rows) {
  return {
    workout_id: workoutId,
    day_name: dayName,
    recommended_day: recommendedDay,
    order,
    notes: null,
    exercises: rows.map(([name, rest, minReps, maxReps, rpe, weight, notes], eIdx) => {
      const exId = `${workoutId}_ex${eIdx + 1}`;
      return {
        exercise_instance_id: exId,
        global_exercise_id: null,
        exercise_name: name,
        order: eIdx + 1,
        rest_seconds: rest,
        coach_notes: notes,
        sets: [1, 2, 3].map((setIndex) => ({
          set_id: `${exId}_set${setIndex}`,
          set_index: setIndex,
          set_type: 'working',
          min_reps: minReps,
          max_reps: maxReps,
          target_rpe: rpe,
          target_weight: weight || null,
        })),
      };
    }),
  };
}

export function getDemoTrainingPlans() {
  return [
    {
      id: 'demo-training-plan',
      user_code: DEMO_USER_CODE,
      user_id: DEMO_CLIENT_ID,
      plan_name: 'Full body strength, phase 1',
      description: 'Three sessions a week building base strength and consistency.',
      goal: 'strength_training',
      difficulty_level: 'beginner',
      duration_weeks: 4,
      weekly_frequency: 3,
      status: 'active',
      active_from: isoDate(10),
      active_until: isoDate(-18),
      // Current builder shape (see utils/trainingPlanSchema.js serializePlan / hydratePlan).
      plan_structure: {
        template_id: 'tpl_demo_full_body',
        name: 'Full body strength, phase 1',
        duration_weeks: 4,
        is_repeating: true,
        intensity_level: 'moderate',
        focus: 'strength_training',
        pre_workout_notes: 'Warm up 5-10 minutes before each session.',
        weeks: [
          {
            week_id: 'week_demo_1',
            week_number: 1,
            focus: 'Learn the movements',
            // day_number 1 = Sunday. Full body A on Sun/Thu, B on Tue.
            days: [
              { slot_id: 'slot_demo_1', day_number: 1, kind: 'workout', workout_id: 'wk_demo_a', notes: null },
              { slot_id: 'slot_demo_2', day_number: 2, kind: 'rest', workout_id: null, notes: null },
              { slot_id: 'slot_demo_3', day_number: 3, kind: 'workout', workout_id: 'wk_demo_b', notes: null },
              { slot_id: 'slot_demo_4', day_number: 4, kind: 'rest', workout_id: null, notes: null },
              { slot_id: 'slot_demo_5', day_number: 5, kind: 'workout', workout_id: 'wk_demo_a', notes: null },
              { slot_id: 'slot_demo_6', day_number: 6, kind: 'rest', workout_id: null, notes: null },
              { slot_id: 'slot_demo_7', day_number: 7, kind: 'rest', workout_id: null, notes: null },
            ],
          },
        ],
        workouts: [
          demoWorkout('wk_demo_a', 'Full body A', 'sunday', 1, [
            ['Goblet squat', 90, 8, 10, 7, '16', 'Elbows inside the knees at the bottom.'],
            ['Push up', 90, 6, 8, 7, '', 'Knees down if form breaks.'],
            ['Bent over row', 90, 8, 10, 7, '20', null],
            ['Plank', 60, 1, 1, 6, '', 'Hold 30-45 seconds per set.'],
          ]),
          demoWorkout('wk_demo_b', 'Full body B', 'tuesday', 2, [
            ['Romanian deadlift', 120, 8, 10, 7, '30', 'Soft knees, hinge from the hips.'],
            ['Overhead press', 90, 6, 8, 7, '15', null],
            ['Lat pulldown', 90, 10, 12, 7, '25', null],
          ]),
        ],
      },
      created_at: isoStamp(10),
    },
  ];
}

export function getDemoTrainingLogs() {
  return [
    {
      id: 'demo-training-log-1',
      user_code: DEMO_USER_CODE,
      user_id: DEMO_CLIENT_ID,
      session_date: isoDate(2),
      session_duration_minutes: 48,
      perceived_exertion: 7,
      exercises_logged: [
        {
          name: 'Goblet squat',
          sets: [
            { reps: 10, weight: 16 },
            { reps: 10, weight: 16 },
            { reps: 9, weight: 18 },
          ],
        },
        {
          name: 'Push up',
          sets: [
            { reps: 8, weight: 0 },
            { reps: 8, weight: 0 },
            { reps: 7, weight: 0 },
          ],
        },
        {
          name: 'Bent over row',
          sets: [
            { reps: 10, weight: 20 },
            { reps: 10, weight: 20 },
            { reps: 10, weight: 22 },
          ],
        },
        {
          name: 'Plank',
          sets: [
            { reps: 1, weight: 0 },
            { reps: 1, weight: 0 },
          ],
        },
      ],
      notes: 'Full body A — squats progressing; felt strong on the last set.',
    },
    {
      id: 'demo-training-log-2',
      user_code: DEMO_USER_CODE,
      user_id: DEMO_CLIENT_ID,
      session_date: isoDate(5),
      session_duration_minutes: 42,
      perceived_exertion: 6,
      exercises_logged: [
        {
          name: 'Romanian deadlift',
          sets: [
            { reps: 10, weight: 30 },
            { reps: 10, weight: 30 },
            { reps: 8, weight: 32 },
          ],
        },
        {
          name: 'Overhead press',
          sets: [
            { reps: 8, weight: 15 },
            { reps: 8, weight: 15 },
            { reps: 7, weight: 15 },
          ],
        },
        {
          name: 'Lat pulldown',
          sets: [
            { reps: 12, weight: 25 },
            { reps: 12, weight: 25 },
          ],
        },
      ],
      notes: 'Full body B — hinge form felt clean; kept press weight steady.',
    },
  ];
}

export function getDemoTrainingReminders() {
  return [];
}
